import React, { useState } from 'react';
import { Box, Collapse, IconButton } from '@material-ui/core';
import VectorArrowRightIcon from 'ui-elements/icons/FsVectorArrow';
import { FsDirection, TransactionType } from 'globals/types';
import defaultLocales from 'globals/locales/default';
import {
  getTransactionsData,
  PaginationInfo,
} from 'models/detached-actions/report';
import useTheme from '@material-ui/core/styles/useTheme';
import FsCategoryIconLoader from 'ui-elements/image-loaders/FsCategoryIconLoader';
import { useReportContext } from 'contexts/report/ReportContext';
import { FsExpenditureTableTransactionRowDataProps } from '../FsExpenditureTableTransactionRow/FsExpenditureTableTransactionRow';
import {
  Value,
  CategoryName,
  LastValue,
  LastValueBox,
  RowBox,
} from './FsExpenditureTableCategoryRow.style-elements';
import {
  getCategoryIconStyle,
  getIconButtonStyle,
  getTableRowStyle,
} from './FsExpenditureTableCategoryRow.style';
import { getConditions } from './FsExpenditureTableCategoryRow.utils';
import FsExpenditureTableTransactionsContainer from '../FsExpenditureTableTransactionsContainer';

export interface ExpenditureTableCategoryRowDataProps {
  categoryName: string;
  category?: string;
  value: number;
  displayValue: string;
  percentage: string;
  subcategories?: ExpenditureTableCategoryRowDataProps[];
  areTransactions: boolean;
  areSubcategories: boolean;
  id: string;
}

export interface FsExpenditureTableCategoryRowProps
  extends ExpenditureTableCategoryRowDataProps {
  subcategories?: FsExpenditureTableCategoryRowProps[];
  isSubCategory?: boolean;
  onClickHandler?: () => void;
  transactionType?: TransactionType;
}

const FsExpenditureTableCategoryRow: React.FC<FsExpenditureTableCategoryRowProps> = ({
  categoryName,
  value,
  displayValue,
  percentage,
  subcategories,
  isSubCategory,
  areTransactions,
  areSubcategories,
  transactionType,
}) => {
  const {
    report: { userId, activeAccountType },
  } = useReportContext();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [
    isTransactionListPending,
    setIsTransactionListPending,
  ] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<
    Array<FsExpenditureTableTransactionRowDataProps>
  >([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>();
  const [transactionsPageNumber, setTransactionsPageNumber] = React.useState(1);

  // conditions
  const { isSomethingToShow } = getConditions({
    areTransactions,
    value,
    isSubCategory,
    areSubcategories,
  });

  // styles
  const tableRowStyle = getTableRowStyle({
    isOpen,
    isSubCategory,
    isSomethingToShow,
    theme,
  });
  const iconButtonStyle = getIconButtonStyle(theme);
  const categoryIconStyle = getCategoryIconStyle(theme);

  // methods, handlers
  const loadTransactions = async (): Promise<void> => {
    setIsTransactionListPending(true);
    const transactionsData = await getTransactionsData({
      userId,
      activeAccountType,
    });
    if (transactionsData?.transactionArray)
      setTransactions(transactionsData.transactionArray);
    if (transactionsData?.paginationInfo)
      setPaginationInfo(transactionsData?.paginationInfo);
    setIsTransactionListPending(false);
  };

  const handleClick = async (): Promise<void> => {
    // if we just proceed close action (so when isOpen is 'true')
    // or we expand available subcategories
    if (isOpen || (!isSubCategory && areSubcategories)) {
      setIsOpen(!isOpen);
      return;
    }
    // if we need to load transactions
    setIsOpen(!isOpen);
    await loadTransactions();
    setTransactionsPageNumber(1);
  };

  const onPageChangeHandler = async (pageNumber: number): Promise<void> => {
    // TODO: waiting for API
    await loadTransactions();
    setTransactionsPageNumber(pageNumber);
  };

  // parts
  const categoryNamePartWithCollapseButton = (
    <Box display="flex" alignItems="center">
      <IconButton
        disabled={!isSomethingToShow}
        aria-label={defaultLocales.moreInfo}
        style={iconButtonStyle}
        onClick={handleClick}
      >
        <VectorArrowRightIcon
          direction={isOpen ? FsDirection.UP : FsDirection.DOWN}
          disabled={!isSomethingToShow}
        />
      </IconButton>
      <Box style={categoryIconStyle}>
        <FsCategoryIconLoader categoryName={categoryName} />
      </Box>
      <CategoryName>{categoryName}</CategoryName>
    </Box>
  );

  const transactionsPart = !areSubcategories && (
    <FsExpenditureTableTransactionsContainer
      transactionType={transactionType}
      categoryName={categoryName}
      isTransactionListPending={isTransactionListPending}
      transactions={transactions}
      transactionsPageNumber={transactionsPageNumber}
      onPageChange={onPageChangeHandler}
      paginationInfo={paginationInfo}
    />
  );

  const subCategoriesPart =
    !isSubCategory &&
    subcategories?.map(({ id, ...props }) => (
      <FsExpenditureTableCategoryRow
        data-test="expenditure-table-sub-category-row"
        key={id}
        id={id}
        isSubCategory
        transactionType={transactionType}
        {...props}
      />
    ));

  return (
    <Box>
      <RowBox
        style={tableRowStyle}
        onClick={isSomethingToShow ? handleClick : undefined}
      >
        <Box>{categoryNamePartWithCollapseButton}</Box>
        <Box display="flex" alignItems="center">
          <Box>
            <Value>{displayValue}</Value>
          </Box>
          <LastValueBox>
            <LastValue>{percentage}</LastValue>
          </LastValueBox>
        </Box>
      </RowBox>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {transactionsPart}
        {subCategoriesPart}
      </Collapse>
    </Box>
  );
};

export default FsExpenditureTableCategoryRow;
